import { CloseButton, Dialog, Icon, Input, Portal, Stack, Text } from '@chakra-ui/react';
import { toaster } from './ui/toaster';
import { useEffect, useState } from 'react';
import { HelpCircle } from 'react-feather';
import { useSaveProjectStateMutation, useSetActiveProjectStateMutation } from '../lib/api/GraphAPI';
import { newInitialProjectState, setProjectState } from '../lib/state/slices/ProjectStateSlice';
import { useAppDispatch, useAppSelector } from '../lib/hooks';
import { useNavigate } from 'react-router-dom';
import { Tooltip } from './ui/tooltip';
import StyledButton from './ui/styledButton';

export const NewProjectModal = (props: { open: boolean; setOpen: (open: boolean) => void }) => {
  const [saveView, { isError: saveProjectIsError, error: saveProjectError }] =
    useSaveProjectStateMutation();

  const [setActive, { isError: setActiveIsError, error: setActiveError }] =
    useSetActiveProjectStateMutation();

  const state = useAppSelector((state) => state.viewReducer);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [text, setText] = useState('');

  const handleClose = () => {
    setText('');
    props.setOpen(false);
  };

  const handleSubmit = () => {
    if (state.isAutosaveActive) {
      saveView(state);
    }

    const newProjectState = newInitialProjectState({ name: text, isAutosaveActive: true });

    dispatch(setProjectState(newProjectState));

    navigate('/research');

    saveView(newProjectState);
    setActive(newProjectState.id);

    handleClose();
  };

  useEffect(() => {
    if (saveProjectIsError) {
      toaster.create({
        title: 'Error while saving current project',
        description: `Something went wrong while saving the current project: ${saveProjectError}`,
        type: 'error',
        closable: true,
      });
    }
  }, [saveProjectIsError]);

  useEffect(() => {
    if (setActiveIsError) {
      toaster.create({
        title: 'Error while switching projects',
        description: `Something went wrong while switching to the new project: ${setActiveError}`,
        type: 'error',
        closable: true,
      });
    }
  }, [setActiveIsError]);

  return (
    <Dialog.Root lazyMount open={props.open} onOpenChange={(e) => props.setOpen(e.open)}>
      <Portal>
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content backgroundColor="midCardBG">
            <Dialog.Header>
              <Dialog.Title>
                <Stack direction="row" alignItems="center">
                  <Text color="primaryText">New Project</Text>
                  <Tooltip
                    content="A project contains your graphs, folders and search information"
                    placement="right"
                  >
                    <Icon color="secondaryText">
                      <HelpCircle width={20} height={20} />
                    </Icon>
                  </Tooltip>
                </Stack>
              </Dialog.Title>
            </Dialog.Header>
            <Dialog.Body>
              <Stack gap="2">
                <Input
                  variant="subtle"
                  outline="none"
                  backgroundColor="cardDefault"
                  color="secondaryText"
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  type="text"
                  placeholder="Name of your project"
                />
              </Stack>
            </Dialog.Body>
            <Dialog.Footer>
              <StyledButton variant="outline" text="Cancel" onClick={handleClose} />
              <StyledButton variant="solid" text="Create" onClick={handleSubmit} />
            </Dialog.Footer>
            <Dialog.CloseTrigger>
              <CloseButton size={'sm'} />
            </Dialog.CloseTrigger>
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  );
};
